import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "../utils/axiosInstance";
import {
  Container,
  Card,
  Row,
  Col,
  Table,
  Button,
  Form,
  Modal,
  Badge,
  InputGroup,
  Spinner,
  Toast,
  ToastContainer,
  Pagination,
  OverlayTrigger,
  Tooltip,
  ProgressBar,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";

const API_URL = `${process.env.REACT_APP_API_URL || ''}/urubyiruko_rukora`;

const initialForm = {
  amazina: "",
  imyaka: "",
  igitsina: "Gabo",
  umurimo: "",
  nimero_ya_telephone: "",
  aderesi: "",
  ubuhanga: "Ikindi",
};

function UrubyirukoRukora() {
  const theme = {
    bg: "linear-gradient(135deg, #0a0e17 0%, #0f172a 40%, #0c1220 100%)",
    bgSolid: "#0a0e17",
    surface: "rgba(15, 23, 42, 0.85)",
    surfaceElevated: "rgba(30, 41, 59, 0.65)",
    text: "#f1f5f9",
    textMuted: "#94a3b8",
    textDimmed: "#64748b",
    border: "rgba(148, 163, 184, 0.12)",
    borderHover: "rgba(34, 211, 238, 0.3)",
    blur: "blur(24px)",
    primary: "#22d3ee",
    primaryDark: "#0891b2",
    primaryGlow: "rgba(34, 211, 238, 0.25)",
    primarySoft: "rgba(34, 211, 238, 0.08)",
    danger: "#f87171",
    dangerSoft: "rgba(248, 113, 113, 0.12)",
    warning: "#fbbf24",
    warningSoft: "rgba(251, 191, 36, 0.12)",
    success: "#34d399",
    successSoft: "rgba(52, 211, 153, 0.12)",
    purple: "#a78bfa",
    purpleSoft: "rgba(167, 139, 250, 0.12)",
    blue: "#60a5fa",
    blueSoft: "rgba(96, 165, 250, 0.12)",
    shadow: "0 8px 32px rgba(0,0,0,0.4)",
    shadowLg: "0 20px 60px rgba(0,0,0,0.5)",
    shadowGlow: "0 0 40px rgba(34,211,238,0.15)",
    radius: "16px",
    radiusSm: "12px",
    radiusXs: "8px",
  };

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [detailItem, setDetailItem] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [editId, setEditId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", bg: "success", icon: "" });
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterGender, setFilterGender] = useState("All");
  const [filterSkill, setFilterSkill] = useState("All");
  const [sortConfig, setSortConfig] = useState({ key: "amazina", direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState("table");
  const [animateCards, setAnimateCards] = useState(false);
  const itemsPerPage = 8;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterGender, filterSkill]);

  useEffect(() => {
    if (viewMode === "grid") {
      setAnimateCards(false);
      const timer = setTimeout(() => setAnimateCards(true), 50);
      return () => clearTimeout(timer);
    }
  }, [viewMode, currentPage]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL);
      setData(res.data || []);
    } catch {
      showToast("Habaye ikibazo mu kubarura amakuru.", "danger", "bi-exclamation-triangle");
    } finally {
      setLoading(false);
    }
  };

  const showToast = useCallback((message, bg = "success", icon = "bi-check-circle") => {
    setToast({ show: true, message, bg, icon });
    setTimeout(() => setToast((prev) => ({ ...prev, show: false })), 4800);
  }, []);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const uniqueSkills = useMemo(() => {
    const skills = new Set(data.map((item) => item.ubuhanga).filter(Boolean));
    return ["All", ...Array.from(skills)];
  }, [data]);

  const processedData = useMemo(() => {
    let result = [...data];
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      result = result.filter(
        (item) =>
          item.amazina?.toLowerCase().includes(lower) ||
          item.umurimo?.toLowerCase().includes(lower) ||
          item.nimero_ya_telephone?.includes(lower) ||
          item.aderesi?.toLowerCase().includes(lower)
      );
    }
    if (filterGender !== "All") result = result.filter((item) => item.igitsina === filterGender);
    if (filterSkill !== "All") result = result.filter((item) => item.ubuhanga === filterSkill);
    if (sortConfig.key) {
      result.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];
        if (sortConfig.key === "imyaka") {
          aVal = parseInt(aVal) || 0;
          bVal = parseInt(bVal) || 0;
        } else {
          aVal = (aVal ?? "").toString().toLowerCase();
          bVal = (bVal ?? "").toString().toLowerCase();
        }
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [data, searchTerm, filterGender, filterSkill, sortConfig]);

  const totalPages = Math.ceil(processedData.length / itemsPerPage);
  const paginatedData = processedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const maleCount = data.filter((x) => x.igitsina === "Gabo").length;
  const femaleCount = data.filter((x) => x.igitsina === "Gore").length;
  const malePercent = data.length > 0 ? ((maleCount / data.length) * 100).toFixed(0) : 0;
  const femalePercent = data.length > 0 ? ((femaleCount / data.length) * 100).toFixed(0) : 0;

  const handleSave = async () => {
    const newErrors = {};
    if (!form.amazina?.trim()) newErrors.amazina = "Izina rirakenewe";
    if (!form.umurimo?.trim()) newErrors.umurimo = "Umurimo urakenewe";
    if (form.imyaka && (isNaN(form.imyaka) || form.imyaka < 0 || form.imyaka > 120))
      newErrors.imyaka = "Imyaka ntiyemewe";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API_URL}/${editId}`, form);
        showToast("Byahinduwe neza!", "success", "bi-check-circle");
      } else {
        await axios.post(API_URL, form);
        showToast("Byongewe neza!", "success", "bi-plus-circle");
      }
      setShowForm(false);
      setForm(initialForm);
      setEditId(null);
      setErrors({});
      loadData();
    } catch {
      showToast("Habaye ikibazo.", "danger", "bi-exclamation-triangle");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/${itemToDelete.id}`);
      showToast("Byasibwe neza.", "warning", "bi-trash");
      loadData();
    } catch {
      showToast("Gusiba byananiranye.", "danger", "bi-exclamation-triangle");
    } finally {
      setDeleting(false);
      setShowDelete(false);
    }
  };

  const openEdit = (item) => {
    setForm({ ...item });
    setEditId(item.id);
    setErrors({});
    setShowForm(true);
  };

  const openAdd = () => {
    setForm(initialForm);
    setEditId(null);
    setErrors({});
    setShowForm(true);
  };

  const openDetail = (item) => {
    setDetailItem(item);
    setShowDetail(true);
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name) => {
    if (!name) return theme.primary;
    const colors = ["#22d3ee", "#a78bfa", "#f87171", "#34d399", "#fbbf24", "#60a5fa", "#fb923c", "#e879f9"];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) return <i className="bi bi-chevron-expand ms-1 opacity-25"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-chevron-up ms-1" style={{ color: theme.primary }}></i>
    ) : (
      <i className="bi bi-chevron-down ms-1" style={{ color: theme.primary }}></i>
    );
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800&display=swap');

        * {
          font-family: 'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
          -webkit-tap-highlight-color: transparent;
        }

        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,0.2);
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover { background: rgba(148,163,184,0.35); }

        body {
          background: ${theme.bgSolid};
          color: ${theme.text};
          overflow-x: hidden;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        .app-wrapper {
          background: ${theme.bg};
          min-height: 100vh;
          position: relative;
        }

        .app-wrapper::before {
          content: '';
          position: fixed;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(ellipse at 20% 50%, rgba(34,211,238,0.04) 0%, transparent 60%),
                      radial-gradient(ellipse at 80% 20%, rgba(167,139,250,0.03) 0%, transparent 50%),
                      radial-gradient(ellipse at 50% 80%, rgba(248,113,113,0.02) 0%, transparent 50%);
          pointer-events: none;
          z-index: 0;
        }

        .content-layer { position: relative; z-index: 1; }

        /* Glass Card */
        .glass-card {
          background: ${theme.surface};
          backdrop-filter: ${theme.blur};
          -webkit-backdrop-filter: ${theme.blur};
          border: 1px solid ${theme.border};
          border-radius: ${theme.radius};
          box-shadow: ${theme.shadow};
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          color: ${theme.text};
          overflow: hidden;
        }

        .glass-card-interactive {
          cursor: pointer;
        }

        .glass-card-interactive:hover {
          border-color: ${theme.borderHover};
          box-shadow: ${theme.shadowLg}, ${theme.shadowGlow};
          transform: translateY(-4px) scale(1.01);
        }

        .glass-card-interactive:active {
          transform: translateY(-1px) scale(0.995);
        }

        /* Stat Cards */
        .stat-card {
          position: relative;
          overflow: hidden;
        }

        .stat-card::after {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 100px;
          height: 100px;
          border-radius: 50%;
          opacity: 0.05;
          transform: translate(30%, -30%);
          transition: all 0.4s ease;
        }

        .stat-card:hover::after {
          opacity: 0.1;
          transform: translate(20%, -20%) scale(1.2);
        }

        .stat-icon-wrapper {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.3rem;
          transition: all 0.3s ease;
        }

        .stat-card:hover .stat-icon-wrapper {
          transform: scale(1.1) rotate(-5deg);
        }

        .stat-value {
          font-size: 2rem;
          font-weight: 800;
          line-height: 1;
          letter-spacing: -0.02em;
        }

        /* Buttons */
        .btn-modern-primary {
          background: linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark});
          border: none;
          border-radius: ${theme.radiusSm};
          color: #000;
          font-weight: 600;
          padding: 0.7rem 1.5rem;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          box-shadow: 0 4px 15px rgba(34,211,238,0.3);
          position: relative;
          overflow: hidden;
        }

        .btn-modern-primary::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s ease;
        }

        .btn-modern-primary:hover::before { left: 100%; }
        .btn-modern-primary:hover {
          transform: translateY(-2px) scale(1.02);
          box-shadow: 0 8px 25px rgba(34,211,238,0.4);
          color: #000;
        }
        .btn-modern-primary:active {
          transform: translateY(0) scale(0.98);
        }

        .btn-modern-outline {
          background: transparent;
          border: 1px solid ${theme.border};
          color: ${theme.text};
          border-radius: ${theme.radiusSm};
          font-weight: 500;
          padding: 0.7rem 1.5rem;
          transition: all 0.3s ease;
        }

        .btn-modern-outline:hover {
          background: ${theme.primarySoft};
          border-color: ${theme.primary};
          color: ${theme.primary};
          transform: translateY(-1px);
        }

        .btn-modern-danger {
          background: linear-gradient(135deg, ${theme.danger}, #dc2626);
          border: none;
          color: #fff;
          border-radius: ${theme.radiusSm};
          font-weight: 600;
          padding: 0.7rem 1.5rem;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(248,113,113,0.3);
        }

        .btn-modern-danger:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(248,113,113,0.4);
          color: #fff;
        }

        .btn-icon {
          width: 40px;
          height: 40px;
          border-radius: ${theme.radiusXs};
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: 1px solid ${theme.border};
          background: transparent;
          color: ${theme.textMuted};
          transition: all 0.25s ease;
          padding: 0;
        }

        .btn-icon:hover { background: ${theme.surfaceElevated}; color: ${theme.text}; }
        .btn-icon-edit:hover { color: ${theme.warning}; border-color: ${theme.warning}; background: ${theme.warningSoft}; }
        .btn-icon-delete:hover { color: ${theme.danger}; border-color: ${theme.danger}; background: ${theme.dangerSoft}; }
        .btn-icon-view:hover { color: ${theme.primary}; border-color: ${theme.primary}; background: ${theme.primarySoft}; }

        /* Form Controls */
        .form-modern {
          background: rgba(15,23,42,0.6) !important;
          border: 1px solid ${theme.border} !important;
          color: ${theme.text} !important;
          border-radius: ${theme.radiusSm} !important;
          padding: 0.8rem 1rem !important;
          font-size: 0.95rem !important;
          transition: all 0.3s ease !important;
        }

        .form-modern:focus {
          background: rgba(15,23,42,0.9) !important;
          border-color: ${theme.primary} !important;
          box-shadow: 0 0 0 4px ${theme.primaryGlow} !important;
          color: ${theme.text} !important;
        }

        .form-modern::placeholder {
          color: ${theme.textDimmed} !important;
        }

        .form-modern option {
          background: #1e293b;
          color: ${theme.text};
        }

        .form-label-modern {
          color: ${theme.textMuted};
          font-weight: 600;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.5rem;
        }

        /* Table */
        .modern-table {
          border-collapse: separate;
          border-spacing: 0;
          width: 100%;
        }

        .modern-table thead th {
          background: rgba(15,23,42,0.95) !important;
          color: ${theme.textMuted} !important;
          border-bottom: 1px solid ${theme.border} !important;
          font-weight: 700;
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.08em;
          padding: 1rem 1rem !important;
          position: sticky;
          top: 0;
          z-index: 10;
          cursor: pointer;
          user-select: none;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .modern-table thead th:hover {
          color: ${theme.primary} !important;
          background: rgba(15,23,42,1) !important;
        }

        .modern-table tbody td {
          padding: 0.85rem 1rem !important;
          border-bottom: 1px solid rgba(148,163,184,0.06) !important;
          vertical-align: middle;
          color: ${theme.text};
          font-size: 0.9rem;
          background: transparent !important;
        }

        .modern-table tbody tr {
          transition: all 0.2s ease;
        }

        .modern-table tbody tr:hover {
          background: rgba(34,211,238,0.03) !important;
        }

        .modern-table tbody tr:hover td {
          background: transparent !important;
        }

        /* Avatar */
        .avatar {
          width: 40px;
          height: 40px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 0.8rem;
          color: #000;
          flex-shrink: 0;
          transition: all 0.3s ease;
        }

        .avatar-lg {
          width: 64px;
          height: 64px;
          border-radius: 18px;
          font-size: 1.2rem;
        }

        /* Badge */
        .badge-modern {
          padding: 0.45em 0.85em;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.78rem;
          letter-spacing: 0.02em;
          border: none;
        }

        .badge-gabo {
          background: ${theme.blueSoft};
          color: ${theme.blue};
        }

        .badge-gore {
          background: rgba(236,72,153,0.12);
          color: #f472b6;
        }

        .badge-skill {
          background: ${theme.purpleSoft};
          color: ${theme.purple};
        }

        /* Grid Cards */
        .person-card {
          border-radius: ${theme.radius};
          background: ${theme.surface};
          border: 1px solid ${theme.border};
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
          overflow: hidden;
          position: relative;
        }

        .person-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, ${theme.primary}, ${theme.purple});
          opacity: 0;
          transition: opacity 0.3s ease;
        }

        .person-card:hover {
          border-color: ${theme.borderHover};
          box-shadow: ${theme.shadowLg}, ${theme.shadowGlow};
          transform: translateY(-6px);
        }

        .person-card:hover::before { opacity: 1; }

        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .card-animate {
          animation: fadeInUp 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
          opacity: 0;
        }

        /* Modal */
        .modal-modern .modal-content {
          background: rgba(15,23,42,0.98);
          backdrop-filter: ${theme.blur};
          border: 1px solid ${theme.border};
          border-radius: 20px;
          box-shadow: ${theme.shadowLg};
          color: ${theme.text};
          overflow: hidden;
        }

        .modal-modern .modal-header {
          border-bottom: 1px solid ${theme.border};
          padding: 1.5rem 1.8rem;
        }

        .modal-modern .modal-body { padding: 1.8rem; }

        .modal-modern .modal-footer {
          border-top: 1px solid ${theme.border};
          padding: 1.2rem 1.8rem;
        }

        .modal-modern .btn-close {
          filter: invert(1) grayscale(1) brightness(2);
          opacity: 0.5;
        }

        .modal-modern .btn-close:hover { opacity: 1; }

        /* Pagination */
        .pagination-modern .page-item .page-link {
          background: transparent;
          border: 1px solid ${theme.border};
          color: ${theme.textMuted};
          border-radius: 10px !important;
          margin: 0 3px;
          padding: 0.5rem 0.85rem;
          font-weight: 500;
          transition: all 0.25s ease;
          min-width: 40px;
          text-align: center;
        }

        .pagination-modern .page-item .page-link:hover {
          background: ${theme.primarySoft};
          border-color: ${theme.primary};
          color: ${theme.primary};
        }

        .pagination-modern .page-item.active .page-link {
          background: linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark});
          border-color: ${theme.primary};
          color: #000;
          font-weight: 700;
          box-shadow: 0 4px 12px rgba(34,211,238,0.3);
        }

        .pagination-modern .page-item.disabled .page-link {
          opacity: 0.3;
          background: transparent;
        }

        /* Toast */
        .toast-modern {
          border-radius: 14px !important;
          border: 1px solid ${theme.border} !important;
          backdrop-filter: blur(20px) !important;
          box-shadow: ${theme.shadowLg} !important;
          overflow: hidden;
        }

        /* Search bar */
        .search-wrapper {
          position: relative;
        }

        .search-wrapper .search-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          color: ${theme.textDimmed};
          font-size: 1.1rem;
          z-index: 2;
          pointer-events: none;
        }

        .search-wrapper input {
          padding-left: 48px !important;
        }

        /* View toggle */
        .view-toggle {
          display: flex;
          border-radius: ${theme.radiusSm};
          border: 1px solid ${theme.border};
          overflow: hidden;
        }

        .view-toggle-btn {
          background: transparent;
          border: none;
          color: ${theme.textMuted};
          padding: 0.6rem 1rem;
          transition: all 0.25s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .view-toggle-btn:hover { color: ${theme.text}; background: ${theme.surfaceElevated}; }
        .view-toggle-btn.active {
          background: ${theme.primarySoft};
          color: ${theme.primary};
        }

        /* Progress bars */
        .progress-modern {
          height: 6px;
          background: rgba(148,163,184,0.1);
          border-radius: 10px;
          overflow: hidden;
        }

        .progress-modern .progress-bar {
          border-radius: 10px;
          transition: width 1s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Responsive */
        @media (max-width: 768px) {
          .stat-value { font-size: 1.5rem; }
          .stat-icon-wrapper { width: 42px; height: 42px; font-size: 1.1rem; }
          .hide-mobile { display: none !important; }
          .modern-table { font-size: 0.82rem; }
          .modern-table thead th { padding: 0.7rem 0.6rem !important; font-size: 0.7rem; }
          .modern-table tbody td { padding: 0.7rem 0.6rem !important; }
          .avatar { width: 34px; height: 34px; border-radius: 10px; font-size: 0.7rem; }
          .btn-icon { width: 34px; height: 34px; }
        }

        @media (max-width: 576px) {
          .header-title { font-size: 1.4rem !important; }
          .header-subtitle { font-size: 0.85rem !important; }
        }

        /* Pulse animation for loading */
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }

        .skeleton {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          background: ${theme.surfaceElevated};
          border-radius: ${theme.radiusXs};
        }

        /* Floating action button for mobile */
        .fab {
          position: fixed;
          bottom: 24px;
          right: 24px;
          width: 60px;
          height: 60px;
          border-radius: 18px;
          background: linear-gradient(135deg, ${theme.primary}, ${theme.primaryDark});
          border: none;
          color: #000;
          font-size: 1.5rem;
          display: none;
          align-items: center;
          justify-content: center;
          box-shadow: 0 8px 30px rgba(34,211,238,0.4);
          z-index: 1000;
          transition: all 0.3s ease;
        }

        .fab:hover { transform: scale(1.1); box-shadow: 0 12px 40px rgba(34,211,238,0.5); }
        .fab:active { transform: scale(0.95); }

        @media (max-width: 768px) {
          .fab { display: flex; }
          .desktop-add-btn { display: none !important; }
        }

        /* Name cell truncate */
        .name-truncate {
          max-width: 180px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        @media (max-width: 576px) {
          .name-truncate { max-width: 100px; }
        }

        /* Empty state */
        .empty-state {
          padding: 4rem 2rem;
          text-align: center;
        }

        .empty-state-icon {
          width: 80px;
          height: 80px;
          border-radius: 20px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 2rem;
          margin-bottom: 1.5rem;
        }
      `}</style>

      <div className="app-wrapper">
        <div className="content-layer">
          <Container fluid className="py-4 py-md-5 px-3 px-md-4 px-lg-5">

            {/* ── HEADER ────────────────────────────────────────── */}
            <div className="glass-card mb-4 mb-md-5">
              <div className="p-4 p-md-5">
                <Row className="align-items-center g-4">
                  <Col xs={12} lg={7}>
                    <div className="d-flex align-items-start gap-3 gap-md-4">
                      <div
                        className="stat-icon-wrapper flex-shrink-0"
                        style={{
                          background: theme.primarySoft,
                          width: 60,
                          height: 60,
                          borderRadius: 18,
                          fontSize: "1.6rem",
                        }}
                      >
                        <i className="bi bi-briefcase-fill" style={{ color: theme.primary }}></i>
                      </div>
                      <div>
                        <h1
                          className="fw-bold mb-2 header-title"
                          style={{ fontSize: "2rem", lineHeight: 1.2, letterSpacing: "-0.02em" }}
                        >
                          Urubyiruko Rufite Akazi
                        </h1>
                        <p className="mb-0 header-subtitle" style={{ color: theme.textMuted, fontSize: "1rem", lineHeight: 1.6 }}>
                          Shyiramo urubyiruko ufite rufite akazi mudugudu wanyu – reba imibare ku bakobwa n'abahungu
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col xs={12} lg={5}>
                    <div className="d-flex flex-wrap gap-3 justify-content-lg-end">
                      <Button
                        className="btn-modern-outline d-flex align-items-center gap-2"
                        onClick={loadData}
                        disabled={loading}
                      >
                        <i className={`bi bi-arrow-repeat ${loading ? "spin" : ""}`}></i>
                        <span>Refresh</span>
                      </Button>
                      <Button
                        className="btn-modern-primary d-flex align-items-center gap-2 desktop-add-btn"
                        onClick={openAdd}
                      >
                        <i className="bi bi-plus-lg"></i>
                        <span>Ongeraho Mushya</span>
                      </Button>
                    </div>
                  </Col>
                </Row>
              </div>
            </div>

            {/* ── STATS ─────────────────────────────────────────── */}
            <Row className="g-3 g-md-4 mb-4 mb-md-5">
              {[
                {
                  title: "Igiteranyo Cyose",
                  value: data.length,
                  icon: "bi-people-fill",
                  color: theme.primary,
                  bg: theme.primarySoft,
                  subtitle: "Abafite akazi",
                },
                {
                  title: "Abahungu",
                  value: maleCount,
                  icon: "bi-gender-male",
                  color: theme.blue,
                  bg: theme.blueSoft,
                  subtitle: `${malePercent}% by'igiteranyo`,
                  percent: malePercent,
                  percentColor: theme.blue,
                },
                {
                  title: "Abakobwa",
                  value: femaleCount,
                  icon: "bi-gender-female",
                  color: "#f472b6",
                  bg: "rgba(236,72,153,0.1)",
                  subtitle: `${femalePercent}% by'igiteranyo`,
                  percent: femalePercent,
                  percentColor: "#f472b6",
                },
                {
                  title: "Ibyavuye muri filteri",
                  value: processedData.length,
                  icon: "bi-funnel-fill",
                  color: theme.purple,
                  bg: theme.purpleSoft,
                  subtitle: "Abagaragara",
                },
              ].map((stat, i) => (
                <Col xs={6} lg={3} key={i}>
                  <div className="glass-card glass-card-interactive stat-card h-100">
                    <div className="p-3 p-md-4">
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        <div
                          className="stat-icon-wrapper"
                          style={{ background: stat.bg }}
                        >
                          <i className={stat.icon} style={{ color: stat.color }}></i>
                        </div>
                        <span className="stat-value" style={{ color: stat.color }}>
                          {stat.value}
                        </span>
                      </div>
                      <h6 className="fw-bold mb-1" style={{ fontSize: "0.85rem" }}>{stat.title}</h6>
                      <p className="mb-0" style={{ color: theme.textDimmed, fontSize: "0.78rem" }}>
                        {stat.subtitle}
                      </p>
                      {stat.percent !== undefined && (
                        <div className="progress-modern mt-3">
                          <div
                            className="progress-bar"
                            style={{
                              width: `${stat.percent}%`,
                              background: `linear-gradient(90deg, ${stat.percentColor}, ${stat.percentColor}88)`,
                            }}
                          ></div>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>

            {/* ── FILTERS ───────────────────────────────────────── */}
            <div className="glass-card mb-4 mb-md-5">
              <div className="p-3 p-md-4">
                <Row className="g-3 align-items-center">
                  <Col xs={12} md={5} lg={4}>
                    <div className="search-wrapper">
                      <i className="bi bi-search search-icon"></i>
                      <Form.Control
                        placeholder="Shakisha amazina, akazi, nimero..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="form-modern"
                        style={{ paddingLeft: "48px" }}
                      />
                    </div>
                  </Col>
                  <Col xs={6} md={3} lg={2}>
                    <Form.Select
                      value={filterGender}
                      onChange={(e) => setFilterGender(e.target.value)}
                      className="form-modern"
                    >
                      <option value="All">Igitsina Cyose</option>
                      <option value="Gabo">Gabo</option>
                      <option value="Gore">Gore</option>
                    </Form.Select>
                  </Col>
                  <Col xs={6} md={3} lg={3}>
                    <Form.Select
                      value={filterSkill}
                      onChange={(e) => setFilterSkill(e.target.value)}
                      className="form-modern"
                    >
                      <option value="All">Ubuhanga Bwose</option>
                      {uniqueSkills
                        .filter((s) => s !== "All")
                        .map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                    </Form.Select>
                  </Col>
                  <Col xs={12} md={1} lg={3}>
                    <div className="d-flex justify-content-end align-items-center gap-2">
                      <span className="d-none d-lg-inline" style={{ color: theme.textDimmed, fontSize: "0.85rem" }}>
                        Igaragaza:
                      </span>
                      <div className="view-toggle">
                        <button
                          className={`view-toggle-btn ${viewMode === "table" ? "active" : ""}`}
                          onClick={() => setViewMode("table")}
                          title="Table view"
                        >
                          <i className="bi bi-list-ul"></i>
                        </button>
                        <button
                          className={`view-toggle-btn ${viewMode === "grid" ? "active" : ""}`}
                          onClick={() => setViewMode("grid")}
                          title="Grid view"
                        >
                          <i className="bi bi-grid-3x3-gap"></i>
                        </button>
                      </div>
                    </div>
                  </Col>
                </Row>

                {(searchTerm || filterGender !== "All" || filterSkill !== "All") && (
                  <div className="d-flex flex-wrap gap-2 mt-3 pt-3" style={{ borderTop: `1px solid ${theme.border}` }}>
                    <span style={{ color: theme.textDimmed, fontSize: "0.85rem" }} className="d-flex align-items-center me-1">
                      <i className="bi bi-funnel me-1"></i> Active:
                    </span>
                    {searchTerm && (
                      <Badge
                        className="badge-modern d-flex align-items-center gap-1"
                        style={{ background: theme.primarySoft, color: theme.primary, cursor: "pointer" }}
                        onClick={() => setSearchTerm("")}
                      >
                        "{searchTerm}" <i className="bi bi-x"></i>
                      </Badge>
                    )}
                    {filterGender !== "All" && (
                      <Badge
                        className="badge-modern d-flex align-items-center gap-1"
                        style={{ background: theme.blueSoft, color: theme.blue, cursor: "pointer" }}
                        onClick={() => setFilterGender("All")}
                      >
                        {filterGender} <i className="bi bi-x"></i>
                      </Badge>
                    )}
                    {filterSkill !== "All" && (
                      <Badge
                        className="badge-modern d-flex align-items-center gap-1"
                        style={{ background: theme.purpleSoft, color: theme.purple, cursor: "pointer" }}
                        onClick={() => setFilterSkill("All")}
                      >
                        {filterSkill} <i className="bi bi-x"></i>
                      </Badge>
                    )}
                    <Badge
                      className="badge-modern d-flex align-items-center gap-1"
                      style={{ background: theme.dangerSoft, color: theme.danger, cursor: "pointer" }}
                      onClick={() => {
                        setSearchTerm("");
                        setFilterGender("All");
                        setFilterSkill("All");
                      }}
                    >
                      Siba Byose <i className="bi bi-x-lg" style={{ fontSize: "0.65rem" }}></i>
                    </Badge>
                  </div>
                )}
              </div>
            </div>

            {/* ── DATA VIEW ─────────────────────────────────────── */}
            {loading ? (
              <div className="glass-card">
                <div className="text-center py-5 my-4">
                  <div className="position-relative d-inline-block mb-4">
                    <Spinner
                      animation="border"
                      style={{
                        color: theme.primary,
                        width: "3rem",
                        height: "3rem",
                        borderWidth: "3px",
                      }}
                    />
                  </div>
                  <p className="fw-medium mb-0" style={{ color: theme.textMuted }}>
                    Tegereza gato, amakuru arimo gushakishwa...
                  </p>
                </div>
              </div>
            ) : viewMode === "table" ? (
              /* ─── TABLE VIEW ───────────────────────────────────── */
              <div className="glass-card overflow-hidden">
                <div className="table-responsive">
                  <table className="modern-table mb-0 w-100">
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}>#</th>
                        <th onClick={() => handleSort("amazina")}>
                          <span className="d-flex align-items-center">
                            Amazina <SortIcon column="amazina" />
                          </span>
                        </th>
                        <th onClick={() => handleSort("imyaka")} className="hide-mobile">
                          <span className="d-flex align-items-center">
                            Imyaka <SortIcon column="imyaka" />
                          </span>
                        </th>
                        <th>Igitsina</th>
                        <th onClick={() => handleSort("umurimo")}>
                          <span className="d-flex align-items-center">
                            Umurimo <SortIcon column="umurimo" />
                          </span>
                        </th>
                        <th className="hide-mobile">Telephone</th>
                        <th className="hide-mobile">Ubuhanga</th>
                        <th style={{ width: 140 }} className="text-end">
                          Ibikorwa
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.map((item, idx) => (
                        <tr key={item.id}>
                          <td>
                            <span style={{ color: theme.textDimmed, fontWeight: 500, fontSize: "0.82rem" }}>
                              {(currentPage - 1) * itemsPerPage + idx + 1}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="avatar"
                                style={{ background: getAvatarColor(item.amazina) }}
                              >
                                {getInitials(item.amazina)}
                              </div>
                              <div className="name-truncate">
                                <div className="fw-semibold" title={item.amazina}>
                                  {item.amazina}
                                </div>
                                <div
                                  className="d-md-none"
                                  style={{ color: theme.textDimmed, fontSize: "0.78rem" }}
                                >
                                  {item.umurimo || "—"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="hide-mobile">
                            <span style={{ color: theme.textMuted }}>
                              {item.imyaka ? `${item.imyaka} yrs` : "—"}
                            </span>
                          </td>
                          <td>
                            <span className={`badge-modern ${item.igitsina === "Gabo" ? "badge-gabo" : "badge-gore"}`}>
                              <i className={`bi ${item.igitsina === "Gabo" ? "bi-gender-male" : "bi-gender-female"} me-1`}></i>
                              {item.igitsina}
                            </span>
                          </td>
                          <td>
                            <span className="fw-medium">{item.umurimo || "—"}</span>
                          </td>
                          <td className="hide-mobile">
                            {item.nimero_ya_telephone ? (
                              <a
                                href={`tel:${item.nimero_ya_telephone}`}
                                style={{ color: theme.primary, textDecoration: "none", fontWeight: 500 }}
                              >
                                <i className="bi bi-telephone me-1" style={{ fontSize: "0.8rem" }}></i>
                                {item.nimero_ya_telephone}
                              </a>
                            ) : (
                              <span style={{ color: theme.textDimmed }}>—</span>
                            )}
                          </td>
                          <td className="hide-mobile">
                            <span className="badge-modern badge-skill">{item.ubuhanga || "—"}</span>
                          </td>
                          <td>
                            <div className="d-flex justify-content-end gap-2">
                              <OverlayTrigger placement="top" overlay={<Tooltip>Reba</Tooltip>}>
                                <button className="btn-icon btn-icon-view" onClick={() => openDetail(item)}>
                                  <i className="bi bi-eye"></i>
                                </button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Hindura</Tooltip>}>
                                <button className="btn-icon btn-icon-edit" onClick={() => openEdit(item)}>
                                  <i className="bi bi-pencil"></i>
                                </button>
                              </OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Siba</Tooltip>}>
                                <button
                                  className="btn-icon btn-icon-delete"
                                  onClick={() => {
                                    setItemToDelete(item);
                                    setShowDelete(true);
                                  }}
                                >
                                  <i className="bi bi-trash3"></i>
                                </button>
                              </OverlayTrigger>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {paginatedData.length === 0 && (
                        <tr>
                          <td colSpan="8">
                            <div className="empty-state">
                              <div
                                className="empty-state-icon"
                                style={{ background: theme.surfaceElevated }}
                              >
                                <i className="bi bi-inbox" style={{ color: theme.textDimmed }}></i>
                              </div>
                              <h5 className="fw-bold mb-2" style={{ color: theme.textMuted }}>
                                Nta makuru abonetse
                              </h5>
                              <p style={{ color: theme.textDimmed, maxWidth: 400, margin: "0 auto" }}>
                                Nta byagaragaye. Gerageza guhindura amagambo yo gushakisha cyangwa filteri.
                              </p>
                              <Button className="btn-modern-primary mt-3" onClick={openAdd}>
                                <i className="bi bi-plus-lg me-2"></i>
                                Ongeraho Uwambere
                              </Button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              /* ─── GRID VIEW ────────────────────────────────────── */
              <Row className="g-3 g-md-4">
                {paginatedData.length === 0 && (
                  <Col xs={12}>
                    <div className="glass-card">
                      <div className="empty-state">
                        <div className="empty-state-icon" style={{ background: theme.surfaceElevated }}>
                          <i className="bi bi-inbox" style={{ color: theme.textDimmed }}></i>
                        </div>
                        <h5 className="fw-bold mb-2" style={{ color: theme.textMuted }}>
                          Nta makuru abonetse
                        </h5>
                        <p style={{ color: theme.textDimmed }}>
                          Gerageza guhindura filteri cyangwa ongeraho umuntu mushya.
                        </p>
                        <Button className="btn-modern-primary mt-3" onClick={openAdd}>
                          <i className="bi bi-plus-lg me-2"></i>
                          Ongeraho Uwambere
                        </Button>
                      </div>
                    </div>
                  </Col>
                )}
                {paginatedData.map((item, idx) => (
                  <Col xs={12} sm={6} lg={4} xl={3} key={item.id}>
                    <div
                      className={`person-card h-100 ${animateCards ? "card-animate" : ""}`}
                      style={{ animationDelay: `${idx * 0.07}s` }}
                    >
                      <div className="p-4">
                        <div className="d-flex align-items-start justify-content-between mb-3">
                          <div
                            className="avatar avatar-lg"
                            style={{ background: getAvatarColor(item.amazina) }}
                          >
                            {getInitials(item.amazina)}
                          </div>
                          <div className="d-flex gap-1">
                            <button className="btn-icon btn-icon-view" style={{ width: 34, height: 34 }} onClick={() => openDetail(item)}>
                              <i className="bi bi-eye" style={{ fontSize: "0.85rem" }}></i>
                            </button>
                            <button className="btn-icon btn-icon-edit" style={{ width: 34, height: 34 }} onClick={() => openEdit(item)}>
                              <i className="bi bi-pencil" style={{ fontSize: "0.85rem" }}></i>
                            </button>
                            <button
                              className="btn-icon btn-icon-delete"
                              style={{ width: 34, height: 34 }}
                              onClick={() => {
                                setItemToDelete(item);
                                setShowDelete(true);
                              }}
                            >
                              <i className="bi bi-trash3" style={{ fontSize: "0.85rem" }}></i>
                            </button>
                          </div>
                        </div>

                        <h6 className="fw-bold mb-1" style={{ fontSize: "1rem" }}>
                          {item.amazina}
                        </h6>
                        <p className="mb-3" style={{ color: theme.textMuted, fontSize: "0.88rem" }}>
                          {item.umurimo || "N'umurimo ntago afite"}
                        </p>

                        <div className="d-flex flex-wrap gap-2 mb-3">
                          <span className={`badge-modern ${item.igitsina === "Gabo" ? "badge-gabo" : "badge-gore"}`}>
                            <i className={`bi ${item.igitsina === "Gabo" ? "bi-gender-male" : "bi-gender-female"} me-1`}></i>
                            {item.igitsina}
                          </span>
                          {item.imyaka && (
                            <span className="badge-modern" style={{ background: theme.surfaceElevated, color: theme.textMuted }}>
                              {item.imyaka} yrs
                            </span>
                          )}
                          {item.ubuhanga && (
                            <span className="badge-modern badge-skill">{item.ubuhanga}</span>
                          )}
                        </div>

                        <div className="pt-3" style={{ borderTop: `1px solid ${theme.border}` }}>
                          {item.nimero_ya_telephone && (
                            <a
                              href={`tel:${item.nimero_ya_telephone}`}
                              className="d-flex align-items-center gap-2 text-decoration-none mb-2"
                              style={{ color: theme.primary, fontSize: "0.88rem" }}
                            >
                              <i className="bi bi-telephone" style={{ fontSize: "0.82rem" }}></i>
                              {item.nimero_ya_telephone}
                            </a>
                          )}
                          {item.aderesi && (
                            <div
                              className="d-flex align-items-center gap-2"
                              style={{ color: theme.textDimmed, fontSize: "0.85rem" }}
                            >
                              <i className="bi bi-geo-alt" style={{ fontSize: "0.82rem" }}></i>
                              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {item.aderesi}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}

            {/* ── PAGINATION ────────────────────────────────────── */}
            {totalPages > 1 && (
              <div className="d-flex flex-wrap justify-content-between align-items-center mt-4 gap-3">
                <p className="mb-0" style={{ color: theme.textDimmed, fontSize: "0.85rem" }}>
                  Igaragaza {(currentPage - 1) * itemsPerPage + 1}–
                  {Math.min(currentPage * itemsPerPage, processedData.length)} muri {processedData.length}
                </p>
                <Pagination className="pagination-modern mb-0">
                  <Pagination.Prev
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <i className="bi bi-chevron-left"></i>
                  </Pagination.Prev>

                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
                    if (endPage - startPage + 1 < maxVisible) {
                      startPage = Math.max(1, endPage - maxVisible + 1);
                    }

                    if (startPage > 1) {
                      pages.push(
                        <Pagination.Item key={1} onClick={() => setCurrentPage(1)}>
                          1
                        </Pagination.Item>
                      );
                      if (startPage > 2) pages.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
                    }

                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <Pagination.Item
                          key={i}
                          active={i === currentPage}
                          onClick={() => setCurrentPage(i)}
                        >
                          {i}
                        </Pagination.Item>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) pages.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
                      pages.push(
                        <Pagination.Item key={totalPages} onClick={() => setCurrentPage(totalPages)}>
                          {totalPages}
                        </Pagination.Item>
                      );
                    }

                    return pages;
                  })()}

                  <Pagination.Next
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    <i className="bi bi-chevron-right"></i>
                  </Pagination.Next>
                </Pagination>
              </div>
            )}
          </Container>
        </div>

        {/* ── FAB (mobile) ────────────────────────────────────── */}
        <button className="fab" onClick={openAdd} aria-label="Add new person">
          <i className="bi bi-plus-lg"></i>
        </button>
      </div>

      {/* ── TOAST ──────────────────────────────────────────────── */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        <Toast
          onClose={() => setToast((prev) => ({ ...prev, show: false }))}
          show={toast.show}
          delay={4800}
          autohide
          className="toast-modern"
          style={{
            background: "rgba(15,23,42,0.95)",
            backdropFilter: "blur(20px)",
            color: theme.text,
          }}
        >
          <Toast.Body className="d-flex align-items-center gap-3 p-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-3"
              style={{
                width: 38,
                height: 38,
                background:
                  toast.bg === "success"
                    ? theme.successSoft
                    : toast.bg === "warning"
                    ? theme.warningSoft
                    : theme.dangerSoft,
                flexShrink: 0,
              }}
            >
              <i
                className={`bi ${toast.icon || "bi-info-circle"}`}
                style={{
                  color:
                    toast.bg === "success"
                      ? theme.success
                      : toast.bg === "warning"
                      ? theme.warning
                      : theme.danger,
                  fontSize: "1.1rem",
                }}
              ></i>
            </div>
            <span className="fw-medium">{toast.message}</span>
            <button
              type="button"
              className="btn-close btn-close-white ms-auto"
              style={{ fontSize: "0.65rem", opacity: 0.5 }}
              onClick={() => setToast((prev) => ({ ...prev, show: false }))}
            ></button>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* ── ADD / EDIT MODAL ───────────────────────────────────── */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg" centered className="modal-modern">
        <Modal.Header closeButton>
          <div className="d-flex align-items-center gap-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-3"
              style={{
                width: 44,
                height: 44,
                background: editId ? theme.warningSoft : theme.primarySoft,
              }}
            >
              <i
                className={`bi ${editId ? "bi-pencil-square" : "bi-person-plus"}`}
                style={{ color: editId ? theme.warning : theme.primary, fontSize: "1.2rem" }}
              ></i>
            </div>
            <div>
              <Modal.Title className="fw-bold" style={{ fontSize: "1.2rem" }}>
                {editId ? "Hindura Amakuru" : "Ongeraho Urubyiruko"}
              </Modal.Title>
              <p className="mb-0" style={{ color: theme.textDimmed, fontSize: "0.85rem" }}>
                {editId ? "Hindura amakuru y'umuntu" : "Uzuza amakuru y'umuntu mushya"}
              </p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row className="g-4">
              <Col md={6}>
                <Form.Label className="form-label-modern">
                  <i className="bi bi-person me-2" style={{ color: theme.primary }}></i>
                  Amazina
                </Form.Label>
                <Form.Control
                  value={form.amazina}
                  onChange={(e) => {
                    setForm({ ...form, amazina: e.target.value });
                    if (errors.amazina) setErrors({ ...errors, amazina: "" });
                  }}
                  isInvalid={!!errors.amazina}
                  className="form-modern"
                  placeholder="Andika amazina..."
                />
                <Form.Control.Feedback type="invalid">{errors.amazina}</Form.Control.Feedback>
              </Col>
              <Col md={6}>
                <Form.Label className="form-label-modern">
                  <i className="bi bi-briefcase me-2" style={{ color: theme.primary }}></i>
                  Umurimo
                </Form.Label>
                <Form.Control
                  value={form.umurimo}
                  onChange={(e) => {
                    setForm({ ...form, umurimo: e.target.value });
                    if (errors.umurimo) setErrors({ ...errors, umurimo: "" });
                  }}
                  isInvalid={!!errors.umurimo}
                  className="form-modern"
                  placeholder="Andika umurimo..."
                />
                <Form.Control.Feedback type="invalid">{errors.umurimo}</Form.Control.Feedback>
              </Col>
              <Col xs={6} md={4}>
                <Form.Label className="form-label-modern">
                  <i className="bi bi-calendar3 me-2" style={{ color: theme.primary }}></i>
                  Imyaka
                </Form.Label>
                <Form.Control
                  type="number"
                  value={form.imyaka}
                  onChange={(e) => {
                    setForm({ ...form, imyaka: e.target.value });
                    if (errors.imyaka) setErrors({ ...errors, imyaka: "" });
                  }}
                  isInvalid={!!errors.imyaka}
                  className="form-modern"
                  placeholder="e.g. 25"
                  min="0"
                  max="120"
                />
                <Form.Control.Feedback type="invalid">{errors.imyaka}</Form.Control.Feedback>
              </Col>
              <Col xs={6} md={4}>
                <Form.Label className="form-label-modern">
                  <i className="bi bi-gender-ambiguous me-2" style={{ color: theme.primary }}></i>
                  Igitsina
                </Form.Label>
                <Form.Select
                  value={form.igitsina}
                  onChange={(e) => setForm({ ...form, igitsina: e.target.value })}
                  className="form-modern"
                >
                  <option value="Gabo">Gabo</option>
                  <option value="Gore">Gore</option>
                </Form.Select>
              </Col>
              <Col xs={12} md={4}>
                <Form.Label className="form-label-modern">
                  <i className="bi bi-telephone me-2" style={{ color: theme.primary }}></i>
                  Telephone
                </Form.Label>
                <Form.Control
                  value={form.nimero_ya_telephone}
                  onChange={(e) => setForm({ ...form, nimero_ya_telephone: e.target.value })}
                  className="form-modern"
                  placeholder="e.g. 0788123456"
                />
              </Col>
              <Col md={6}>
                <Form.Label className="form-label-modern">
                  <i className="bi bi-geo-alt me-2" style={{ color: theme.primary }}></i>
                  Aderesi
                </Form.Label>
                <Form.Control
                  value={form.aderesi}
                  onChange={(e) => setForm({ ...form, aderesi: e.target.value })}
                  className="form-modern"
                  placeholder="Andika aderesi..."
                />
              </Col>
              <Col md={6}>
                <Form.Label className="form-label-modern">
                  <i className="bi bi-lightning me-2" style={{ color: theme.primary }}></i>
                  Ubuhanga
                </Form.Label>
                <Form.Select
                  value={form.ubuhanga}
                  onChange={(e) => setForm({ ...form, ubuhanga: e.target.value })}
                  className="form-modern"
                >
                  <option value="Ikindi">Ikindi</option>
                  <option value="Ubumenyingiro">Ubumenyingiro</option>
                  <option value="Ikoranabuhanga">Ikoranabuhanga</option>
                  <option value="Ubucuruzi">Ubucuruzi</option>
                  <option value="Ubuhinzi">Ubuhinzi</option>
                  <option value="Ubworozi">Ubworozi</option>
                </Form.Select>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-modern-outline" onClick={() => setShowForm(false)} disabled={saving}>
            <i className="bi bi-x-lg me-2"></i>
            Bireke
          </Button>
          <Button className="btn-modern-primary" onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Tegereza...
              </>
            ) : (
              <>
                <i className={`bi ${editId ? "bi-check-lg" : "bi-plus-lg"} me-2`}></i>
                {editId ? "Hindura" : "Bika"}
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── DETAIL MODAL ───────────────────────────────────────── */}
      <Modal show={showDetail} onHide={() => setShowDetail(false)} centered className="modal-modern">
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold" style={{ fontSize: "1.1rem" }}>
            <i className="bi bi-person-badge me-2" style={{ color: theme.primary }}></i>
            Amakuru Yose
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {detailItem && (
            <div>
              <div className="text-center mb-4">
                <div
                  className="avatar avatar-lg mx-auto mb-3"
                  style={{
                    background: getAvatarColor(detailItem.amazina),
                    width: 80,
                    height: 80,
                    borderRadius: 22,
                    fontSize: "1.5rem",
                  }}
                >
                  {getInitials(detailItem.amazina)}
                </div>
                <h4 className="fw-bold mb-1">{detailItem.amazina}</h4>
                <p style={{ color: theme.textMuted }}>{detailItem.umurimo || "N'umurimo ntago afite"}</p>
              </div>

              <div
                className="rounded-4 p-4"
                style={{ background: theme.surfaceElevated, border: `1px solid ${theme.border}` }}
              >
                {[
                  { icon: "bi-calendar3", label: "Imyaka", value: detailItem.imyaka ? `${detailItem.imyaka} yrs` : "—" },
                  { icon: "bi-gender-ambiguous", label: "Igitsina", value: detailItem.igitsina, badge: true },
                  { icon: "bi-telephone", label: "Telephone", value: detailItem.nimero_ya_telephone || "—", isPhone: true },
                  { icon: "bi-geo-alt", label: "Aderesi", value: detailItem.aderesi || "—" },
                  { icon: "bi-lightning", label: "Ubuhanga", value: detailItem.ubuhanga || "—" },
                ].map((field, i) => (
                  <div
                    key={i}
                    className={`d-flex align-items-center justify-content-between py-3 ${
                      i > 0 ? "border-top" : ""
                    }`}
                    style={{ borderColor: `${theme.border} !important` }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <i className={field.icon} style={{ color: theme.primary, fontSize: "1rem", width: 20 }}></i>
                      <span style={{ color: theme.textMuted, fontSize: "0.9rem" }}>{field.label}</span>
                    </div>
                    <div className="fw-medium" style={{ fontSize: "0.95rem" }}>
                      {field.badge ? (
                        <span className={`badge-modern ${field.value === "Gabo" ? "badge-gabo" : "badge-gore"}`}>
                          {field.value}
                        </span>
                      ) : field.isPhone && field.value !== "—" ? (
                        <a href={`tel:${field.value}`} style={{ color: theme.primary, textDecoration: "none" }}>
                          {field.value}
                        </a>
                      ) : (
                        field.value
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-modern-outline" onClick={() => setShowDetail(false)}>
            <i className="bi bi-x-lg me-2"></i>
            Funga
          </Button>
          <Button
            className="btn-modern-primary"
            onClick={() => {
              setShowDetail(false);
              if (detailItem) openEdit(detailItem);
            }}
          >
            <i className="bi bi-pencil me-2"></i>
            Hindura
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── DELETE MODAL ───────────────────────────────────────── */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)} centered className="modal-modern" size="sm">
        <Modal.Body className="text-center p-4 p-md-5">
          <div
            className="d-inline-flex align-items-center justify-content-center rounded-4 mb-4"
            style={{
              width: 72,
              height: 72,
              background: theme.dangerSoft,
            }}
          >
            <i className="bi bi-trash3" style={{ color: theme.danger, fontSize: "1.8rem" }}></i>
          </div>
          <h5 className="fw-bold mb-2">Siba Amakuru?</h5>
          <p style={{ color: theme.textMuted, fontSize: "0.95rem" }} className="mb-1">
            Uremeza ko ushaka gusiba:
          </p>
          <p className="fw-bold mb-1" style={{ color: theme.text, fontSize: "1.1rem" }}>
            {itemToDelete?.amazina}
          </p>
          <p className="mb-4" style={{ color: theme.textDimmed, fontSize: "0.82rem" }}>
            <i className="bi bi-exclamation-triangle me-1"></i>
            Ibi ntibigarurwa nyuma yo gusiba.
          </p>
          <div className="d-flex gap-3 justify-content-center">
            <Button
              className="btn-modern-outline flex-fill"
              onClick={() => setShowDelete(false)}
              disabled={deleting}
            >
              Oya, Bireke
            </Button>
            <Button
              className="btn-modern-danger flex-fill"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Tegereza...
                </>
              ) : (
                <>
                  <i className="bi bi-trash3 me-2"></i>
                  Yego, Siba
                </>
              )}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default UrubyirukoRukora;